import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

/**
 * Max order IDs to embed in a URL.
 * Shopify order IDs are ~35 chars each; at 500 IDs the URL is ~20KB,
 * within Vercel (14KB+) and modern browser limits (64KB+).
 */
const MAX_ORDER_IDS_IN_URL = 500

interface OrdersLinkProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  orderIds?: string[]
  productIds?: number[]
  children: ReactNode
  className?: string
}

/**
 * Link that opens the shopify-orders page with a filter.
 * Supports two modes:
 * - order_ids: show exact orders (from cross-sell computed IDs)
 * - product_ids: show orders containing those products (existing dashboard behaviour)
 *
 * If order_ids exceeds MAX_ORDER_IDS_IN_URL, falls back to product_ids.
 * If neither fits, renders children as plain text (no link).
 */
export function OrdersLink({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  orderIds,
  productIds,
  children,
  className,
}: OrdersLinkProps) {
  const hasOrderIds = orderIds && orderIds.length > 0
  const orderIdsOverLimit = hasOrderIds && orderIds.length > MAX_ORDER_IDS_IN_URL
  const hasProductIds = productIds && productIds.length > 0

  // Too many order IDs and no product_ids fallback → plain text
  if (orderIdsOverLimit && !hasProductIds) {
    return <>{children}</>
  }

  const params = new URLSearchParams({
    page: "shopify-orders",
    integration_id: integrationId,
    month_start_utc: monthStartUtc,
    month_end_utc: monthEndUtc,
  })

  if (hasOrderIds && !orderIdsOverLimit) {
    params.set("order_ids", orderIds.join(","))
  } else if (hasProductIds) {
    params.set("product_ids", productIds.join(","))
  }

  return (
    <a
      href={`/?${params}`}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "underline decoration-dotted underline-offset-2 hover:text-primary transition-colors",
        className,
      )}
    >
      {children}
    </a>
  )
}
